import type {GridColDef} from '@mui/x-data-grid'
import {useCallback, useMemo, useState} from 'react'

/**
 * Persist user-resized DataGrid column widths so they survive re-renders and reloads.
 *
 * Background (AB#5380 / Intercom 215474210257405): MUI X DataGrid (community) keeps a
 * resized column width only in its internal state. BasicDataGrid is handed a fresh
 * `columns` array on every parent re-render, which makes the grid re-initialise widths
 * from the column def — so the chosen width "reverts" on the next click/sort/pagination.
 *
 * This hook records widths from `onColumnWidthChange`, stores them in localStorage
 * (the established persistence pattern in this app), and merges them back onto the
 * columns. A stored width wins over `flex` (otherwise flex re-stretches the column).
 */

type StoredWidths = Record<string, number>

// Minimal shape of MUI's columnWidthChange event params we depend on.
export interface ColumnWidthChangeParams {
  colDef: {field: string}
  width: number
}

const STORAGE_PREFIX = 'mbai.dataGridColumnWidths.'

const storageKeyFor = (tableKey: string): string => `${STORAGE_PREFIX}${tableKey}`

const readStoredWidths = (tableKey: string): StoredWidths => {
  if (!tableKey) return {}
  try {
    const raw = window.localStorage.getItem(storageKeyFor(tableKey))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    return Object.entries(parsed as Record<string, unknown>).reduce<StoredWidths>(
      (acc, [field, value]) => {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
          acc[field] = value
        }
        return acc
      },
      {},
    )
  } catch {
    return {}
  }
}

const writeStoredWidths = (tableKey: string, widths: StoredWidths): void => {
  if (!tableKey) return
  try {
    window.localStorage.setItem(storageKeyFor(tableKey), JSON.stringify(widths))
  } catch {
    // localStorage may be unavailable (private mode / quota) — persistence is best-effort.
  }
}

interface UseDataGridColumnWidthsResult {
  columns: GridColDef[]
  onColumnWidthChange: (params: ColumnWidthChangeParams) => void
}

export const useDataGridColumnWidths = (
  tableKey: string,
  columns: GridColDef[],
): UseDataGridColumnWidthsResult => {
  const [widths, setWidths] = useState<StoredWidths>(() => readStoredWidths(tableKey))

  const onColumnWidthChange = useCallback(
    (params: ColumnWidthChangeParams): void => {
      const field = params?.colDef?.field
      const width = params?.width
      if (!field || typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
        return
      }
      setWidths((prev) => {
        if (prev[field] === width) return prev
        const next = {...prev, [field]: width}
        writeStoredWidths(tableKey, next)
        return next
      })
    },
    [tableKey],
  )

  const mergedColumns = useMemo(() => {
    if (Object.keys(widths).length === 0) return columns
    return columns.map((column) => {
      const storedWidth = widths[column.field]
      if (storedWidth === undefined) return column
      // Drop `flex` so the explicit, user-chosen width is authoritative.
      const {flex: _flex, ...rest} = column
      return {...rest, width: storedWidth}
    })
  }, [columns, widths])

  return {columns: mergedColumns, onColumnWidthChange}
}

export default useDataGridColumnWidths
